/**
 * Free-standing Ladder ST 2100 AL (T-2091210) on the outside of a scaffold.
 *
 * Hooks over a beam edge and leans out, foot on whatever surface is below — the
 * way in from the ground, as opposed to the hatch ladder inside a decked bay.
 */

import { Group, Mesh } from "three";
import { CFG, DECK_LIFT, MM, nodeY, PLANK_THICKNESS } from "../../data/haki.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { tubeBetween, V, yawOf } from "../threeHelpers.ts";
import type { RenderContext } from "./context.ts";

/** How far the foot stands out from the edge it hangs on, metres. */
const LADDER_LEAN = 0.6;

/** Clearance below the top so the hooks sit over the beam. */
const HOOK_RISE = 0.06;

export function renderLadders(ctx: RenderContext): void {
  ctx.model.ladders.forEach((ladder, idx) => {
    const mx = (ladder.a.x + ladder.b.x) / 2;
    const mz = (ladder.a.z + ladder.b.z) / 2;
    const group = new Group();

    group.position.set(mx, 0, mz);
    group.rotation.y = yawOf(ladder.dx, ladder.dz) - Math.PI / 2;

    const top = nodeY(ladder.p) + DECK_LIFT + PLANK_THICKNESS;
    const foot = ctx.terrain.groundY(mx + ladder.dx * LADDER_LEAN, mz + ladder.dz * LADDER_LEAN);
    const halfWidth = (CFG.ladder.width * MM) / 2;
    const rise = top - foot;

    for (const edge of [-halfWidth, halfWidth]) {
      group.add(tubeBetween(V(LADDER_LEAN, foot + 0.03, edge), V(0.04, top + 0.02, edge), G.tube27, M.aluD));
      group.add(tubeBetween(V(0.04, top + 0.02, edge), V(-0.06, top + HOOK_RISE, edge), G.tube27, M.cast));
    }

    const rungs = Math.max(1, Math.floor(rise / (CFG.ladder.rung * MM)));
    for (let step = 1; step <= rungs; step++) {
      const t = step / (rungs + 0.5);
      const x = LADDER_LEAN + (0.04 - LADDER_LEAN) * t;
      const y = foot + 0.03 + (top + 0.02 - (foot + 0.03)) * t;
      group.add(tubeBetween(V(x, y, -halfWidth), V(x, y, halfWidth), G.tube27, M.aluD));
    }

    for (const child of group.children) {
      child.userData = { type: "ladder", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);

    // One ST 2100 per 2.1 m of climb — a taller lift needs a second length.
    const count = Math.max(1, Math.ceil(rise / (CFG.ladder.len * MM)));
    ctx.bom.add("Access", CFG.ladder.name, CFG.ladder.art, CFG.ladder.kg, count);
  });
}
